<template>
  <div>
    <!-- Header -->
    <AppHeader />

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Загрузка профиля преподавателя...</p>
    </div>

    <!-- Error state -->
    <div v-if="error" class="error-state">
      <p>{{ error }}</p>
      <router-link to="/teachers" class="retry-button">Вернуться к списку преподавателей</router-link>
    </div>

    <!-- Teacher Profile -->
    <div v-if="teacher && !loading && !error" class="teacher-profile hero-eastern">
      <UISection :narrow="true">
        <template #default>
          <div class="profile-card">
            <div class="profile-media">
              <div class="profile-avatar" v-if="teacher.photo">
                <img :src="teacher.photo" :alt="teacher.name" loading="lazy" decoding="async" />
              </div>
              <div class="profile-avatar profile-avatar--placeholder" v-else>
                <i class="fas fa-user"></i>
              </div>
              <UIButton to="/contacts#contact-methods" size="sm">Записаться на урок</UIButton>
            </div>
            <div class="profile-content">
              <div class="profile-heading">
                <h1>{{ teacher.name }}</h1>
                <p v-if="teacher.label">{{ teacher.label }}</p>
              </div>
              <div class="profile-achievements" v-if="teacher.achievements && teacher.achievements.length">
                <div class="profile-chip" v-for="achievement in teacher.achievements" :key="achievement">
                  {{ achievement }}
                </div>
              </div>
              <div class="profile-contacts" v-if="teacher.email || teacher.social_link">
                <div class="profile-contact-item" v-if="teacher.email">
                  <i class="fas fa-envelope"></i>
                  <span>{{ teacher.email }}</span>
                </div>
                <div class="profile-contact-item" v-if="teacher.social_link">
                  <i class="fas fa-link"></i>
                  <a :href="teacher.social_link" target="_blank">{{ teacher.social_link }}</a>
                </div>
              </div>
            </div>
          </div>
        </template>
      </UISection>
    </div>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>Готовы начать изучение китайского?</h2>
        <p>Свяжитесь с нами и получите бесплатный пробный урок</p>
        <UIButton to="/contacts#contact-methods" variant="outline" class="cta-button">Связаться с нами</UIButton>
      </div>
    </section>

    <!-- Footer -->
    <UIFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import UIButton from '@/components/ui/UIButton.vue'
import UISection from '@/components/ui/UISection.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import { addSchemaScript, getPersonSchema, getBreadcrumbSchema, clearAllSchemas } from '@/utils/schema'

export default {
  name: 'TeacherProfilePage',
  components: {
    AppHeader,
    UIButton,
    UISection,
    UIFooter
  },
  data() {
    return {
      teacher: null,
      loading: true,
      error: null
    }
  },
  async mounted() {
    await this.fetchTeacher();

    // Add animation on scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-up');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.teacher-profile-content > *').forEach(el => {
      observer.observe(el);
    });
  },
  methods: {
    async fetchTeacher() {
      try {
        this.loading = true;
        const teacherId = this.$route.params.id;
        const response = await fetch(`/api/v1/public/teachers/${teacherId}`);
        const data = await response.json();
        
        if (response.ok) {
          // Оставляем только имя (без фамилии)
          const firstName = data.first_name || (data.name ? String(data.name).split(' ')[0] : '');
          this.teacher = {
            ...data,
            name: firstName || data.name || ''
          };
          
          // Обновляем заголовок страницы
          const teacherName = `${data.first_name || ''} ${data.last_name || ''}`.trim() || data.name || 'Преподаватель';
          document.title = `${teacherName} - Преподаватель - Мама я в Китае`;
          // Обновляем мета-тег description
          let metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription && data.bio) {
            const shortBio = data.bio.substring(0, 160).trim();
            metaDescription.setAttribute('content', shortBio || `Преподаватель китайского языка: ${teacherName}`);
          }
          // Обновляем Open Graph теги
          let ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle) {
            ogTitle.setAttribute('content', `${teacherName} - Преподаватель - Мама я в Китае`);
          }
          
          // Добавляем Schema.org разметку для Яндекс
          clearAllSchemas()
          const personSchema = getPersonSchema(data);
          if (personSchema) {
            addSchemaScript(personSchema, 'person');
          }
          
          // Добавляем хлебные крошки
          const breadcrumbs = getBreadcrumbSchema([
            { name: 'Главная', url: window.location.origin },
            { name: 'Преподаватели', url: window.location.origin + '/teachers' },
            { name: teacherName, url: window.location.href }
          ]);
          addSchemaScript(breadcrumbs, 'breadcrumbs');
        } else {
          this.error = 'Учитель не найден';
        }
      } catch (error) {
        this.error = 'Ошибка соединения с сервером';
        console.error('Error fetching teacher:', error);
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.nav-menu a.active {
  color: #e74c3c;
}

.loading-state, .error-state {
  text-align: center;
  padding: 4rem 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #e74c3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.retry-button {
  display: inline-block;
  background: #e74c3c;
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  margin-top: 1rem;
  transition: background 0.3s ease;
}

.retry-button:hover {
  background: #c0392b;
}

.teacher-profile {
  padding: 120px 0 80px;
}

.profile-card {
  display: grid;
  grid-template-columns: 260px 1fr; /* чуть уменьшил ширину медиа */
  gap: 32px; /* было 40px */
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(18px);
  border-radius: 28px;
  box-shadow: 0 28px 80px -40px rgba(15, 23, 42, 0.35);
  padding: 38px; /* компактнее */
  border: 1px solid rgba(255,255,255,0.28);
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s;
}
.profile-card:hover {
  box-shadow: 0 44px 120px -50px rgba(15,23,42,0.55);
}

.profile-card::before {
  content: none;
}

.profile-card > * {
  position: relative;
  z-index: 1;
}

.profile-media {
  display: grid;
  gap: 24px;
  align-content: start;
}

.profile-avatar {
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 24px;
  overflow: hidden;
  background: linear-gradient(160deg, rgba(231, 76, 60, 0.12) 0%, rgba(255,255,255,0.9) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(231, 76, 60, 0.25);
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-avatar--placeholder {
  color: rgba(231, 76, 60, 0.5);
  font-size: 4rem;
}

.profile-content {
  display: grid;
  gap: 18px; /* было 32px, уменьшил вертикальный зазор */
}

.profile-heading h1 {
  font-size: 2.75rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
}

.profile-heading p {
  font-size: 1.1rem;
  color: #6c757d;
}

.profile-achievements {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.profile-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px; /* больше вертикальный отступ для 2 строк */
  border-radius: 999px;
  background: rgba(231, 76, 60, 0.12);
  color: #c0392b;
  font-weight: 600;
  font-size: 0.82rem;
  line-height: 1.2; /* комфортная межстрочность */
  min-height: 28px; /* базовая высота для 1 строки */
  max-width: 100%;
  white-space: normal; /* разрешаем перенос строк */
  overflow-wrap: anywhere; /* переносим длинные слова/URL */
  word-break: break-word;
  border: 1px solid rgba(231, 76, 60, 0.18);
  transition: background 0.2s;
  box-sizing: border-box;
}
.profile-chip:not(:last-child) {
  margin-right: 4px;
}
.profile-chip:hover {
  background: rgba(231, 76, 60, 0.18);
}

.profile-contacts {
  display: grid;
  gap: 12px;
}

.profile-contact-item {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;
  color: #495057;
}

.profile-contact-item i {
  width: 20px;
  color: #e74c3c;
}

.profile-contact-item a {
  color: #e74c3c;
  text-decoration: none;
}

.profile-contact-item a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .profile-card {
    grid-template-columns: 1fr;
    padding: 32px;
  }
}
</style>